/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import qs from "query-string";

import { APPLICATION_TAB_NAMES, JOB_APPLICATION_MATCH_STATUS } from "lookup";
import {
  AuthContext,
  JobsContext,
  MatchContext,
  SearchContext,
} from "context/providers";
import { mapMatchesToHit } from "utils/helpers/match";

import UserCardList from "components/UserCardList";
import Tabs from "components/base/Tabs";
import { TABS } from "context/constants/common";

const useWebAppliesList = () => {
  const { jobOpp, longList, loadJobLongList } = useContext(JobsContext);
  const { user: loggedInUser } = useContext(AuthContext);

  // Reload on every job change: longList holds the previous job's items, so a
  // stale count would otherwise linger when switching jobs.
  useEffect(() => {
    if (loggedInUser.canAccessWebApplys && jobOpp?.id) {
      loadJobLongList(jobOpp.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOpp?.id]);

  return longList;
};

const ApplicationsTabs = () => {
  const { clearSearchState } = useContext(SearchContext);
  const { matches } = useContext(MatchContext);
  const { jobOpp } = useContext(JobsContext);
  const { user: loggedInUser } = useContext(AuthContext);

  // Eager-load the web applies list so the Web Applies tab shows its counter
  // on mount, like the match-based tabs (whose counts come from jobOpp).
  const longList = useWebAppliesList();

  const tabsMatchesRef = useRef({});

  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const RenderCustomNoResults = ({ collectionKey }) => {
    const { searchState } = useContext(SearchContext);

    switch (collectionKey) {
      case "hits": {
        return (
          <p>
            There are no applicants to display. Please{" "}
            <span
              className="text-sky-500 cursor-pointer opacity-70 transition-all select-none hover:opacity-100 underline hover:no-underline"
              onClick={() => {
                clearSearchState({
                  dontClearSearchAgentSkills: true,
                  searchState,
                });
              }}
            >
              Clear Filters
            </span>{" "}
            or change filter criteria.
            <br />
          </p>
        );
      }

      default: {
        return <></>;
      }
    }
  };

  const filtersConfig = {
    Matched: {
      matchStatus: [
        JOB_APPLICATION_MATCH_STATUS.ACCEPTED,
        JOB_APPLICATION_MATCH_STATUS.APPLIED,
        JOB_APPLICATION_MATCH_STATUS.MATCHED,
        JOB_APPLICATION_MATCH_STATUS.OFFERSENT,
        JOB_APPLICATION_MATCH_STATUS.PASSEDON,
        JOB_APPLICATION_MATCH_STATUS.REJECTEDBYCUSTOMER,
        JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER,
        JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMEMBER,
        JOB_APPLICATION_MATCH_STATUS.MOREINFO,
      ],
    },
    Shortlisted: {
      matchStatus: [
        JOB_APPLICATION_MATCH_STATUS.SHORTLISTED,
        JOB_APPLICATION_MATCH_STATUS.INTERESTED,
        JOB_APPLICATION_MATCH_STATUS.INTERESTEDFASTTRACK,
      ],
    },
    Skipped: {
      matchStatus: [JOB_APPLICATION_MATCH_STATUS.SKIPPED],
    },
    PreCandidates: {
      matchStatus: [
        JOB_APPLICATION_MATCH_STATUS.PRESHORTLISTED,
        JOB_APPLICATION_MATCH_STATUS.TKSHORTLISTED,
      ],
    },
  };

  const hasRejectedValue = (array) => {
    const rejectedValues = [
      "REJECTEDBYCUSTOMER",
      "REJECTEDBYMATCHER",
      "REJECTEDBYMEMBER",
    ];

    return array?.some((item) => rejectedValues?.includes(item?.newValue));
  };

  const getItemsCount = (matchStatus, excludedMatchStatuses, tabName) => {
    switch (tabName) {
      case APPLICATION_TAB_NAMES.PRECANDIDATES: {
        const statuses = ["PRESHORTLISTED", "TKSHORTLISTED"];
        const matchedMatches = matches.filter((match) =>
          statuses.includes(match.status)
        );

        return matchedMatches.length;
      }

      case APPLICATION_TAB_NAMES.MATCHED: {
        const statuses = ["MATCHED", "APPLIED", "ACCEPTED", "OFFERSENT"];
        const matchedMatches = matches
          .filter((match) => statuses.includes(match.status))
          .filter((match) => !hasRejectedValue(match.matchHistory));

        return matchedMatches.length;
      }

      case APPLICATION_TAB_NAMES.LONGLISTED: {
        return longList?.items?.length ?? 0;
      }

      case APPLICATION_TAB_NAMES.SHORTLISTED:
      case APPLICATION_TAB_NAMES.SKIPPED: {
        return mapMatchesToHit(
          matches,
          matchStatus,
          tabName,
          excludedMatchStatuses
        )?.length;
      }

      default: {
        return 0;
      }
    }
  };

  const setCount = (item) => {
    const count = getItemsCount(
      item.matchStatus,
      item.excludedMatchStatuses,
      item.title
    );

    const hasMore =
      jobOpp?.matchTabs?.[item.title]?.nextTokensByStatus?.filter(
        (e) => e.nextToken
      )?.length > 0;

    return { ...item, count, hasMore };
  };

  const updateQs = (uniqueId) => {
    const queryString = qs.stringify({
      ...qs.parse(location.search),
      tab: uniqueId,
    });

    navigate(`${location.pathname}?${queryString}`, { replace: true });
  };

  const tabsContent = useMemo(() => {
    const tabs = [
      loggedInUser.canAccessPreCandidates && {
        title: APPLICATION_TAB_NAMES.PRECANDIDATES,
        icon: "circleDotDashed",
        type: "custom",
        uniqueId: TABS.PRECANDIDATES,
        matchStatus: filtersConfig["PreCandidates"].matchStatus,
        tabsMatchesRef,
        renderCustomContent: ({ title: activeTabName, tabsMatchesRef }) => (
          <UserCardList
            key={activeTabName}
            collectionKey="match"
            activeTabName={activeTabName}
            noResultsMessage="There are no Pre-Candidates to display"
            matchStatus={filtersConfig["PreCandidates"].matchStatus}
            allowSorting
            stats
            downloadResults
            tabsMatchesRef={tabsMatchesRef}
          />
        ),
      },
      {
        title: APPLICATION_TAB_NAMES.SEARCHRESULTS,
        icon: "users",
        type: "custom",
        uniqueId: TABS.SEARCHRESULTS,
        tabsMatchesRef,
        renderCustomContent: ({ title: activeTabName, tabsMatchesRef }) => (
          <UserCardList
            key={activeTabName}
            collectionKey="hits"
            activeTabName={activeTabName}
            renderCustomNoResults={
              <RenderCustomNoResults collectionKey="hits" />
            }
            showGeographicalFilter
            showActions
            pagination
            stats
            downloadResults
            tabsMatchesRef={tabsMatchesRef}
          />
        ),
      },
      loggedInUser.canAccessWebApplys && {
        title: APPLICATION_TAB_NAMES.LONGLISTED,
        icon: "focusSimple",
        type: "custom",
        uniqueId: TABS.LONGLISTED,
        tabsMatchesRef,
        renderCustomContent: ({ title: activeTabName, tabsMatchesRef }) => (
          <UserCardList
            key={activeTabName}
            collectionKey="longList"
            activeTabName={activeTabName}
            noResultsMessage="There are no Web Applies to display"
            allowSorting
            showActions
            stats
            downloadResults
            tabsMatchesRef={tabsMatchesRef}
          />
        ),
      },
      {
        title: APPLICATION_TAB_NAMES.SHORTLISTED,
        icon: "focus",
        type: "custom",
        uniqueId: TABS.SHORTLISTED,
        matchStatus: filtersConfig["Shortlisted"].matchStatus,
        tabsMatchesRef,
        renderCustomContent: ({ title: activeTabName, tabsMatchesRef }) => (
          <UserCardList
            key={activeTabName}
            collectionKey="match"
            activeTabName={activeTabName}
            filter={["status"]}
            noResultsMessage="There are no Shortlisted applicants to display"
            matchStatus={filtersConfig["Shortlisted"].matchStatus}
            allowSorting
            stats
            downloadResults
            tabsMatchesRef={tabsMatchesRef}
          />
        ),
      },
      {
        title: APPLICATION_TAB_NAMES.MATCHED,
        icon: "heart",
        type: "custom",
        matchStatus: filtersConfig["Matched"].matchStatus,
        uniqueId: TABS.MATCHED,
        tabsMatchesRef,
        renderCustomContent: ({ title: activeTabName, tabsMatchesRef }) => (
          <UserCardList
            key={activeTabName}
            collectionKey="match"
            activeTabName={activeTabName}
            noResultsMessage="There are no Matched applicants to display"
            filter={["status", "subStatus"]}
            matchStatus={filtersConfig["Matched"].matchStatus}
            allowSorting
            stats
            downloadResults
            tabsMatchesRef={tabsMatchesRef}
          />
        ),
      },
      {
        title: APPLICATION_TAB_NAMES.SKIPPED,
        icon: "skip",
        type: "custom",
        matchStatus: filtersConfig["Skipped"].matchStatus,
        uniqueId: TABS.SKIPPED,
        tabsMatchesRef,
        renderCustomContent: ({ title: activeTabName, tabsMatchesRef }) => (
          <UserCardList
            key={activeTabName}
            collectionKey="match"
            activeTabName={activeTabName}
            noResultsMessage="There are no Skipped applicants to display"
            matchStatus={filtersConfig["Skipped"].matchStatus}
            allowSorting
            stats
            downloadResults
            tabsMatchesRef={tabsMatchesRef}
          />
        ),
      },
    ];

    return tabs.filter((e) => e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getInitialIndex = () => {
    let currentTab = params.get("tab");

    if (currentTab) {
      const index = tabsContent.findIndex((tc) => tc.uniqueId === currentTab);

      if (index !== -1) {
        return index;
      }
    }

    const defaultIndex = tabsContent.findIndex(
      (tab) => tab.title === APPLICATION_TAB_NAMES.SEARCHRESULTS
    );

    return defaultIndex === -1 ? 0 : defaultIndex;
  };

  return (
    <Tabs
      items={tabsContent.map((item) => setCount(item))}
      className="h-full"
      initialIndex={getInitialIndex()}
      updateQs={updateQs}
      rootDataTestId="candidate-tabs-container"
    />
  );
};

export default ApplicationsTabs;
